import { useTranslation } from 'react-i18next';
import { useLocalizationEdit } from '@/contexts/LocalizationEditContext';
import { useMissingKeyReporter } from '@/contexts/MissingKeyReporterContext';
import { useCallback, useRef, useEffect } from 'react';
import type { LocalizationFnOptions } from '@/types/localization-options';
import { isKeysMode } from '@/i18n/config';

/**
 * Custom hook for accessing localizations across namespaces
 * Provides convenient access to common, auth, pages, components, and admin namespaces
 * 
 * SIMPLIFIED 2-MODE ARCHITECTURE:
 * - Production: Uses i18next (JSON → fallback chain)
 * - Edit Mode: Prefers DB values, auto-registers keys for edit panel
 * - Keys Debug: Returns raw keys (dev-only)
 * 
 * SELF-HEALING:
 * When a key falls back to defaultValue (missing translation), it's reported
 * to the MissingKeyReporter for automatic translation.
 * 
 * - Prefers database values over JSON file values
 * - Auto-registers keys as hidden keys for the edit panel (via useEffect to avoid setState during render)
 */
export const useLocalizations = () => {
  const { t: lCommonBase } = useTranslation('common');
  const { t: lAuthBase } = useTranslation('auth');
  const { t: lPagesBase } = useTranslation('pages');
  const { t: lComponentsBase } = useTranslation('components');
  const { t: lFeedbackBase } = useTranslation('feedback');
  
  const { isEditMode, getDbValue, registerHiddenKey } = useLocalizationEdit();
  
  // Self-healing: get reporter and check for self-healed translations
  const { reportMissingKey, translations: selfHealedTranslations } = useMissingKeyReporter();
  
  // Track registered keys to avoid duplicate registrations
  const registeredKeysRef = useRef<Set<string>>(new Set());
  
  // Queue for keys that need registration (processed in useEffect, not during render)
  const pendingRegistrationsRef = useRef<Map<string, { namespace: string; key: string; value: string }>>(new Map());
  
  // Clear registered keys when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      registeredKeysRef.current.clear();
      pendingRegistrationsRef.current.clear();
    }
  }, [isEditMode]);

  // Process pending registrations AFTER render (fixes setState during render warning)
  useEffect(() => {
    if (!isEditMode) return;
    
    const pending = pendingRegistrationsRef.current;
    if (pending.size === 0) return;
    
    // Process all pending registrations
    pending.forEach(({ namespace, key, value }) => {
      registerHiddenKey(namespace, key, value);
    });
    
    // Clear the queue after processing
    pending.clear();
  });

  // Create wrapper that prefers DB value in edit mode, checks self-healed, and queues hidden key registration
  const createLocalizationFn = useCallback(
    (namespace: string, baseFn: (key: string, options?: LocalizationFnOptions) => string) => {
      return (key: string, options?: LocalizationFnOptions): string => {
        // KEYS MODE: Return raw key directly, skip everything else
        if (isKeysMode()) {
          return `${namespace}.${key}`;
        }
        
        // If returnObjects is true, skip DB lookup and use base function
        // (DB stores flat key-value pairs, not nested objects)
        // Note: When returnObjects is used, caller must handle the object return type
        if (options?.returnObjects) {
          return baseFn(key, options) as unknown as string;
        }
        
        const compositeKey = `${namespace}.${key}`;
        
        // Check for self-healed translation first
        const selfHealedValue = selfHealedTranslations.get(compositeKey);
        if (selfHealedValue) {
          let value = selfHealedValue;
          // Handle interpolation
          if (options) {
            Object.entries(options).forEach(([k, v]) => {
              if (k !== 'returnObjects' && k !== 'defaultValue') {
                value = value.replace(new RegExp(`{{${k}}}`, 'g'), String(v));
              }
            });
          }
          return value;
        }
        
        let value: string;
        let fellBackToDefault = false;
        
        if (isEditMode) {
          const rawDbValue = getDbValue(namespace, key);
          // Align with L* components: treat empty/whitespace DB values as "missing"
          const dbValue = (typeof rawDbValue === 'string' && rawDbValue.trim().length > 0)
            ? rawDbValue
            : undefined;
          
          if (dbValue !== undefined) {
            value = dbValue;
            // Handle interpolation if options provided
            if (options) {
              Object.entries(options).forEach(([k, v]) => {
                if (k !== 'returnObjects' && k !== 'defaultValue') {
                  value = value.replace(new RegExp(`{{${k}}}`, 'g'), String(v));
                }
              });
            }
          } else {
            value = baseFn(key, options);
            // Apply defaultValue fallback in edit mode (same as production mode)
            if ((value === key || !value) && options?.defaultValue) {
              value = options.defaultValue;
              if (options) {
                Object.entries(options).forEach(([k, v]) => {
                  if (k !== 'returnObjects' && k !== 'defaultValue') {
                    value = value.replace(new RegExp(`{{${k}}}`, 'g'), String(v));
                  }
                });
              }
            }
          }
          
          // Queue for registration if not already registered (will process in useEffect)
          if (!registeredKeysRef.current.has(compositeKey)) {
            registeredKeysRef.current.add(compositeKey);
            // Register defaultValue when DB is empty so edit UI shows meaningful source text
            const registrationValue = dbValue ?? options?.defaultValue ?? String(baseFn(key));
            pendingRegistrationsRef.current.set(compositeKey, { namespace, key, value: registrationValue });
          }
          
          return value;
        }
        
        // Production mode
        value = baseFn(key, options);
        
        // Check if we fell back to the key itself (missing translation)
        // and have a defaultValue to use
        if ((value === key || !value) && options?.defaultValue) {
          fellBackToDefault = true;
          value = options.defaultValue;
          // Handle interpolation on defaultValue
          if (options) {
            Object.entries(options).forEach(([k, v]) => {
                if (k !== 'returnObjects' && k !== 'defaultValue') {
                value = value.replace(new RegExp(`{{${k}}}`, 'g'), String(v));
              }
            });
          }
        }
        
        // Self-healing: Report missing key for automatic translation
        if (fellBackToDefault && options?.defaultValue) {
          reportMissingKey(namespace, key, options.defaultValue);
        }
        
        return value;
      };
    },
    [isEditMode, getDbValue, selfHealedTranslations, reportMissingKey]
  );

  const lCommon = useCallback(
    (key: string, options?: LocalizationFnOptions) => createLocalizationFn('common', lCommonBase)(key, options),
    [createLocalizationFn, lCommonBase]
  );
  
  const lAuth = useCallback(
    (key: string, options?: LocalizationFnOptions) => createLocalizationFn('auth', lAuthBase)(key, options),
    [createLocalizationFn, lAuthBase]
  );
  
  const lPages = useCallback(
    (key: string, options?: LocalizationFnOptions) => createLocalizationFn('pages', lPagesBase)(key, options),
    [createLocalizationFn, lPagesBase]
  );
  
  const lComponents = useCallback(
    (key: string, options?: LocalizationFnOptions) => createLocalizationFn('components', lComponentsBase)(key, options),
    [createLocalizationFn, lComponentsBase]
  );
  
  const lFeedback = useCallback(
    (key: string, options?: LocalizationFnOptions) => createLocalizationFn('feedback', lFeedbackBase)(key, options),
    [createLocalizationFn, lFeedbackBase]
  );

  return {
    // Direct localization functions for each namespace
    lCommon,
    lAuth,
    lPages,
    lComponents,
    lFeedback,
    
    // Convenience helpers for common use cases (kept for backward compatibility)
    toast: {
      success: (key: string, options?: LocalizationFnOptions) => lFeedback(`toast.${key}`, options),
      error: (key: string, options?: LocalizationFnOptions) => lFeedback(`toast.${key}`, options),
      info: (key: string, options?: LocalizationFnOptions) => lFeedback(`toast.${key}`, options),
      warning: (key: string, options?: LocalizationFnOptions) => lFeedback(`toast.${key}`, options),
    },
    
    validation: (key: string, options?: LocalizationFnOptions) => lFeedback(`validation.${key}`, options),
    
    action: (key: string, options?: LocalizationFnOptions) => lCommon(`actions.${key}`, options),
    
    status: (key: string, options?: LocalizationFnOptions) => lCommon(`status.${key}`, options),
    
    message: (key: string, options?: LocalizationFnOptions) => lFeedback(`messages.${key}`, options),
    
    aria: (key: string, options?: LocalizationFnOptions) => lCommon(`aria.${key}`, options),
  };
};
